import React, { useEffect, useState } from "react";
import { ApiClient } from "../../utils/api-client";
import Header from "../home/header";
import Loading from "../home/Loading";
import { useNavigate } from "react-router";
import { useFormik } from "formik";
import * as Yup from "yup";

const TenderForm = () => {
    const navigate = useNavigate();
    const loginState = sessionStorage.getItem( "loginState" ) ? JSON.parse( sessionStorage.getItem( "loginState" ) ) : null;
    const apiClient = new ApiClient( loginState?.accessToken );

    const [ isLoading, setIsLoading ] = useState( false );
    const [ feedback, setFeedback ] = useState( "" );
    const [ products, setProducts ] = useState( [] );

    const loadData = async () => {
        try {
            const res = await apiClient.get( "products" );
            if ( res.status === 200 && res.data ) setProducts( res.data.data );
        } catch ( e ) {
            console.log( "Error loading products:", e );
        }
    };

    useEffect( () => {
        loadData();
    }, [] );

    // ── number formatting helpers ────────────────────────────────────────────
    const formatNum = ( val ) => {
        if ( val === null || val === undefined || val === "" ) return "";
        const parts = val.toString().split( "." );
        parts[ 0 ] = parts[ 0 ].replace( /\B(?=(\d{3})+(?!\d))/g, "," );
        return parts.join( "." );
    };

    const handleNumericChange = ( field ) => ( e ) => {
        const raw = e.target.value.replace( /,/g, "" );
        if ( !/^\d*(\.\d*)?$/.test( raw ) ) return;
        formik.setFieldValue( field, raw );
    };

    // ── expenses helpers ─────────────────────────────────────────────────────
    const addExpense = () => {
        formik.setFieldValue( "expenses", [
            ...formik.values.expenses,
            { description: "", amount: "" },
        ] );
    };

    const removeExpense = ( idx ) => {
        formik.setFieldValue(
            "expenses",
            formik.values.expenses.filter( ( _, i ) => i !== idx )
        );
    };

    // ── formik ───────────────────────────────────────────────────────────────
    const formik = useFormik( {
        initialValues: {
            title: "",
            organisation: "",
            productIds: [],
            tenderedAmount: "",
            bankLoan: {
                bankName: "",
                loanAmount: "",
                interest: "",
                tenure: "",
            },
            expenses: [ { description: "", amount: "" } ],
        },
        validationSchema: Yup.object( {
            title: Yup.string().required( "Tender title is required" ),
            organisation: Yup.string().required( "Organisation is required" ),
            productIds: Yup.array().min( 1, "Select at least one product" ),
            tenderedAmount: Yup.string().required( "Tendered amount is required" ),
            bankLoan: Yup.object( {
                bankName: Yup.string(),
                loanAmount: Yup.string(),
                interest: Yup.string(),
                tenure: Yup.string(),
            } ),
            expenses: Yup.array().of(
                Yup.object( {
                    description: Yup.string().required( "Description is required" ),
                    amount: Yup.string().required( "Amount is required" ),
                } )
            ),
        } ),
        onSubmit: async ( values ) => {
            setIsLoading( true );
            setFeedback( "" );
            try {
                const res = await apiClient.post( "tenders", values );
                if ( res.status === 201 ) {
                    navigate( "/tenders", { replace: true } );
                } else {
                    setFeedback( "Oops! Something went wrong." );
                }
            } catch ( e ) {
                setFeedback( "Oops! Something went wrong." );
            } finally {
                setIsLoading( false );
            }
        },
    } );

    const handleProductSelect = ( e ) => {
        const selected = Array.from( e.target.selectedOptions ).map( ( o ) => o.value );
        formik.setFieldValue( "productIds", selected );
    };

    return (
        <>
            <Header auth={loginState} />
            {isLoading && <Loading />}
            <section className="flex justify-start items-start">
                <div className="container mx-auto md:mt-8">
                    {feedback && (
                        <div className="w-10/12 md:w-8/12 mx-auto mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-red-700 text-sm" id="form-feedback">{feedback}</div>
                    )}
                    <div className="md:w-10/12 md:mx-auto rounded-xl border border-gray-200 bg-white shadow-md pt-10 px-3 md:px-6">
                        <div className="text-center mx-auto w-full">
                            <p className="text-sm uppercase tracking-wide text-gray-500 text-center">Tender Setup</p>
                            <p className="text-center text-2xl font-semibold text-gray-800 my-3">Register Tender</p>
                            <form
                                id="tender_form"
                                className="text-start w-10/12 md:w-9/12 mx-auto mb-16"
                                onSubmit={formik.handleSubmit}
                            >
                                {/* ── Title ──────────────────────────────────────── */}
                                <div className="form-group my-2 w-10/12 md:w-8/12 lg:w-8/12 mx-auto">
                                    <label htmlFor="title">Tender Title <em className="text-danger">*</em></label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="title"
                                        name="title"
                                        value={formik.values.title}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        placeholder="e.g. Supply of Medical Equipment 2026"
                                    />
                                    {formik.touched.title && formik.errors.title && (
                                        <div className="text-danger small">{formik.errors.title}</div>
                                    )}
                                </div>

                                {/* ── Organisation ────────────────────────────────── */}
                                <div className="form-group my-2 w-10/12 md:w-8/12 lg:w-8/12 mx-auto">
                                    <label htmlFor="organisation">Organisation <em className="text-danger">*</em></label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="organisation"
                                        name="organisation"
                                        value={formik.values.organisation}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        placeholder="e.g. Ministry of Health"
                                    />
                                    {formik.touched.organisation && formik.errors.organisation && (
                                        <div className="text-danger small">{formik.errors.organisation}</div>
                                    )}
                                </div>

                                {/* ── Products (multi-select) ──────────────────────── */}
                                <div className="form-group my-2 w-10/12 md:w-8/12 lg:w-8/12 mx-auto">
                                    <label htmlFor="productIds">
                                        Products <em className="text-danger">*</em>
                                        <span className="text-muted small ms-2">(hold Ctrl / Cmd to select multiple)</span>
                                    </label>
                                    <select
                                        className="form-control"
                                        id="productIds"
                                        name="productIds"
                                        multiple
                                        size={Math.min( products.length || 4, 6 )}
                                        value={formik.values.productIds}
                                        onChange={handleProductSelect}
                                        onBlur={formik.handleBlur}
                                    >
                                        {products.map( ( p ) => (
                                            <option key={p.id} value={p.id}>{p.productName}- {p.packSize}</option>
                                        ) )}
                                    </select>
                                    {formik.touched.productIds && formik.errors.productIds && (
                                        <div className="text-danger small">{formik.errors.productIds}</div>
                                    )}
                                </div>

                                {/* ── Tendered Amount ──────────────────────────────── */}
                                <div className="form-group my-2 w-10/12 md:w-8/12 lg:w-8/12 mx-auto">
                                    <label htmlFor="tenderedAmount">Tendered Amount TZS <em className="text-danger">*</em></label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="tenderedAmount"
                                        name="tenderedAmount"
                                        value={formatNum( formik.values.tenderedAmount )}
                                        onChange={handleNumericChange( "tenderedAmount" )}
                                        onBlur={formik.handleBlur}
                                        inputMode="decimal"
                                        autoComplete="off"
                                    />
                                    {formik.touched.tenderedAmount && formik.errors.tenderedAmount && (
                                        <div className="text-danger small">{formik.errors.tenderedAmount}</div>
                                    )}
                                </div>

                                {/* ── Bank Loan ────────────────────────────────────── */}
                                <div className="w-10/12 md:w-8/12 lg:w-8/12 mx-auto my-4">
                                    <p className="fw-semibold text-base mb-2 border-b pb-1">Bank Loan (optional)</p>
                                    <div className="form-group my-2">
                                        <label htmlFor="bankLoan.bankName">Bank Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="bankLoan.bankName"
                                            name="bankLoan.bankName"
                                            value={formik.values.bankLoan.bankName}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            placeholder="e.g. CRDB Bank"
                                        />
                                    </div>
                                    <div className="form-group my-2">
                                        <label htmlFor="bankLoan.loanAmount">Loan Amount TZS</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="bankLoan.loanAmount"
                                            name="bankLoan.loanAmount"
                                            value={formatNum( formik.values.bankLoan.loanAmount )}
                                            onChange={handleNumericChange( "bankLoan.loanAmount" )}
                                            onBlur={formik.handleBlur}
                                            inputMode="decimal"
                                            autoComplete="off"
                                        />
                                    </div>
                                    <div className="flex gap-4">
                                        <div className="form-group my-2 flex-1">
                                            <label htmlFor="bankLoan.interest">Interest (%)</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="bankLoan.interest"
                                                name="bankLoan.interest"
                                                value={formik.values.bankLoan.interest}
                                                onChange={handleNumericChange( "bankLoan.interest" )}
                                                onBlur={formik.handleBlur}
                                                inputMode="decimal"
                                                autoComplete="off"
                                                placeholder="e.g. 12"
                                            />
                                        </div>
                                        <div className="form-group my-2 flex-1">
                                            <label htmlFor="bankLoan.tenure">Tenure (months)</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="bankLoan.tenure"
                                                name="bankLoan.tenure"
                                                value={formik.values.bankLoan.tenure}
                                                onChange={handleNumericChange( "bankLoan.tenure" )}
                                                onBlur={formik.handleBlur}
                                                inputMode="numeric"
                                                autoComplete="off"
                                                placeholder="e.g. 24"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* ── Expenses Incurred ────────────────────────────── */}
                                <div className="w-10/12 md:w-8/12 lg:w-8/12 mx-auto my-4">
                                    <div className="flex justify-between items-center border-b pb-1 mb-2">
                                        <p className="fw-semibold text-base">Expenses Incurred</p>
                                        <button
                                            type="button"
                                            className="btn btn-sm bg-primary text-white"
                                            onClick={addExpense}
                                        >+ Add</button>
                                    </div>
                                    {formik.values.expenses.map( ( expense, idx ) => (
                                        <div key={idx} className="flex gap-3 items-start my-2">
                                            <div className="form-group flex-1">
                                                {idx === 0 && <label>Description</label>}
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name={`expenses[${idx}].description`}
                                                    value={expense.description}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    placeholder="e.g. Transport"
                                                />
                                                {formik.touched.expenses?.[ idx ]?.description &&
                                                    formik.errors.expenses?.[ idx ]?.description && (
                                                        <div className="text-danger small">
                                                            {formik.errors.expenses[ idx ].description}
                                                        </div>
                                                    )}
                                            </div>
                                            <div className="form-group w-36">
                                                {idx === 0 && <label>Amount TZS</label>}
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name={`expenses[${idx}].amount`}
                                                    value={formatNum( expense.amount )}
                                                    onChange={( e ) => {
                                                        const raw = e.target.value.replace( /,/g, "" );
                                                        if ( !/^\d*(\.\d*)?$/.test( raw ) ) return;
                                                        formik.setFieldValue( `expenses[${idx}].amount`, raw );
                                                    }}
                                                    onBlur={formik.handleBlur}
                                                    inputMode="decimal"
                                                    autoComplete="off"
                                                    placeholder="0"
                                                />
                                                {formik.touched.expenses?.[ idx ]?.amount &&
                                                    formik.errors.expenses?.[ idx ]?.amount && (
                                                        <div className="text-danger small">
                                                            {formik.errors.expenses[ idx ].amount}
                                                        </div>
                                                    )}
                                            </div>
                                            {formik.values.expenses.length > 1 && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm text-danger mt-auto mb-1"
                                                    onClick={() => removeExpense( idx )}
                                                    title="Remove"
                                                >✕</button>
                                            )}
                                        </div>
                                    ) )}
                                </div>

                                {/* ── Submit / Cancel ──────────────────────────────── */}
                                <div className="form-group my-4 w-10/12 md:w-8/12 lg:w-8/12 mx-auto flex gap-3">
                                    <button
                                        type="submit"
                                        className="btn flex-1 bg-success text-white"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? "Saving..." : "Register Tender"}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn flex-1 bg-secondary text-white"
                                        onClick={() => navigate( "/tenders", { replace: true } )}
                                        disabled={isLoading}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};
export default TenderForm;
